"use client"

import { useState } from "react"
import { Meeting } from "@/schema"
import { Button, Calendar, DatePopover, PopoverContent, PopoverTrigger } from "@ui/shad"
import { addMinutes, setHours, setMinutes, startOfDay } from "date-fns"
import { CalendarClock, Loader2 } from "lucide-react"

import { useRescheduleCalendarEvent } from "@/hooks/api/calendar-events"

import { TimeSelect } from "../_ui/time-picker/time-select"
import CalendarPermissionsDialog from "./calendar-permissions-dialog"

interface RescheduleButtonProps {
  meeting: Meeting
}

function getTimeString(date: Date): string {
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`
}

function applyTimeToDate(date: Date, timeString: string): Date {
  const [hours, minutes] = timeString.split(":").map(Number)
  return setMinutes(setHours(date, hours), minutes)
}

export default function RescheduleButton({ meeting }: RescheduleButtonProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date(meeting.start_time))
  const [startTime, setStartTime] = useState<string>(getTimeString(new Date(meeting.start_time)))
  const [endTime, setEndTime] = useState<string>(
    meeting.end_time
      ? getTimeString(new Date(meeting.end_time))
      : getTimeString(addMinutes(new Date(meeting.start_time), 30))
  )

  const reschedule = useRescheduleCalendarEvent()
  const minDate = startOfDay(new Date())

  if (!meeting.calendar_event_id) {
    return null
  }

  function handleSubmit() {
    if (!selectedDate || !meeting.calendar_event_id) return

    const newStartTime = applyTimeToDate(selectedDate, startTime)
    const newEndTime = applyTimeToDate(selectedDate, endTime)

    reschedule.mutate(
      {
        calendarEventId: String(meeting.calendar_event_id),
        startTime: newStartTime,
        endTime: newEndTime,
      },
      {
        onSuccess: () => {
          setIsOpen(false)
        },
      }
    )
  }

  return (
    <>
      <DatePopover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="xs"
            className="text-muted-foreground hover:text-foreground h-6 px-1.5"
            onClick={(e) => e.stopPropagation()}
          >
            <CalendarClock className="size-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" onClick={(e) => e.stopPropagation()}>
          <div className="p-3">
            <h4 className="mb-2 text-sm font-medium">Reschedule meeting</h4>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              startMonth={minDate}
              disabled={{ before: minDate }}
              initialFocus
            />
            <div className="mt-3 flex gap-2">
              <div className="flex-1">
                <label className="text-muted-foreground mb-1 block text-xs">Start</label>
                <TimeSelect value={startTime} onChange={(v) => setStartTime(v)} size="sm" startHour={6} endHour={22} />
              </div>
              <div className="flex-1">
                <label className="text-muted-foreground mb-1 block text-xs">End</label>
                <TimeSelect
                  value={endTime}
                  onChange={(v) => setEndTime(v)}
                  size="sm"
                  startHour={6}
                  endHour={22}
                  minTime={startTime}
                />
              </div>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSubmit} disabled={!selectedDate || reschedule.isPending}>
                {reschedule.isPending && <Loader2 className="animate-spin" />}
                Reschedule
              </Button>
            </div>
          </div>
        </PopoverContent>
      </DatePopover>
      <CalendarPermissionsDialog error={reschedule.error} onClose={reschedule.reset} />
    </>
  )
}
